import dayjs from "dayjs";
import ja from "dayjs/locale/ja";

// TODO(uchida): codegen.ymlのスカラータイプを対応後にDate型に直す

const DAY_OF_WEEK = ["日", "月", "火", "水", "木", "金", "土"] as const;

/**
 * @概要 RFC3339形式のフォーマットのレスポンスを`YYYY年MM月DD日 HH:mm:ss`に変換する関数
 * @example
 * - start : 2021-09-22T15:41:38+09:00
 * - end : 2021年09月22日 15:41:38
 */
export const formatDateToYMDHms = (isoString: Date) => {
  return dayjs(isoString).format("YYYY年MM月DD日 HH:mm:ss");
};

/**
 * @概要 RFC3339形式のフォーマットのレスポンスを`YYYY年MM月DD日 HH:mm`に変換する関数
 * - TODO（kimura） : 本当は string型 不要だけど、mini-detail で string型 で使用していたのでひとまず Union で対応
 *
 * @example
 * - start : 2021-09-22T15:41:38+09:00
 * - end : 2021年09月22日 15:41
 */
export const formatDateToYMDHm = (isoString: Date | string) => {
  return dayjs(isoString).format("YYYY年M月D日 HH:mm");
};

/**
 * @概要 RFC3339形式のフォーマットのレスポンスを`YYYY年MM月DD日`に変換する関数
 * @example
 * - start : 2021-09-22T15:41:38+09:00
 * - end : 2021年09月22日
 */
export const formatDateToYMD = (isoString: Date) => {
  return dayjs(isoString).format("YYYY年M月D日");
};

/**
 * @概要 RFC3339形式のフォーマットのレスポンスを`MM月DD日`に変換する関数
 * @example
 * - start : 2021-09-22T15:41:38+09:00
 * - end : 9月22日
 */
export const formatDateToMD = (isoString: Date) => {
  return dayjs(isoString).format("M月D日");
};

/**
 * @概要 RFC3339形式のフォーマットのレスポンスを`YYYY年`に変換する関数
 * @example
 * - start : 2021-09-22T15:41:38+09:00
 * - end : 2021年
 */
export const formatDateToY = (isoString: Date) => {
  return dayjs(isoString).format("YYYY年");
};

/**
 * @概要 RFC3339形式のフォーマットのレスポンスを`YYYY年MM月DD日（曜日）`に変換する関数
 * @example
 * - start : 2021-09-22T15:41:38+09:00
 * - end : 2021年09月22日（金）
 */
export const formatDateToYMDDay = (isoString: string | Date) => {
  const ymd = dayjs(isoString).format("YYYY年MM月DD日");
  const dayOfWeek = DAY_OF_WEEK[dayjs(isoString).day()];
  return `${ymd}(${dayOfWeek})`;
};

/**
 * @概要 RFC3339形式のフォーマットのレスポンスを`YYYY年MM月DD日（曜日）`に変換する関数
 * @example
 * - start : 2021-09-22T15:41:38+09:00
 * - end : 2021年09月22日（金）
 */
export const formatDateToDayOfWeek = (isoString: Date) => {
  const dayOfWeek = DAY_OF_WEEK[dayjs(isoString).day()];
  return dayOfWeek;
};

/**
 * @概要 RFC3339形式のフォーマットのレスポンスを`YYYY年M月D日(曜日)HH:mm`に変換する関数
 * @example
 * - start : 2021-09-02T15:41:38+09:00
 * - end : 2021年9月2日(金)15:41
 */
export const formatDateToYMDDayHm = (isoString: string | Date) => {
  const ymd = dayjs(isoString).format("YYYY年M月D日");
  const hm = dayjs(isoString).format("HH:mm");
  const dayOfWeek = DAY_OF_WEEK[dayjs(isoString).day()];
  return `${ymd}(${dayOfWeek})${hm}`;
};

/**
 * @概要
 * RFC3339形式のフォーマットの文字列を「開始日時」「終了日時」の2つとして受け取り、
 * `HH:mm〜HH:mm` 形式に変換する関数
 * 終了日時が日を跨いでいた場合は `HH:mm〜MM/DD HH:mm` 形式に変換する
 */
export const formatTimeSpan = (startIsoString: string, endIsoString: string) => {
  const start = dayjs(startIsoString);
  const end = dayjs(endIsoString);
  const startString = start.format("HH:mm");
  const endString =
    start.format("YYYYMMDD") === end.format("YYYYMMDD") ? end.format("HH:mm") : end.format("MM/DD HH:mm");

  return `${startString}〜${endString}`;
};

/**
 * @概要
 * RFC3339形式のフォーマットの文字列を「開始日時」「終了日時」の2つとして受け取り、
 * `M月D日(ddd) HH:mm〜HH:mm` 形式に変換する関数
 * 終了日時が日を跨いでいた場合は `M月D日(ddd) HH:mm〜M月D日(ddd) HH:mm` 形式に変換する
 */
export const formatTimeSpanJaWeek = (startIsoString: string, endIsoString: string) => {
  const start = dayjs(startIsoString).locale(ja);
  const end = dayjs(endIsoString).locale(ja);
  const startString = start.format("M月D日(ddd) HH:mm");
  const endString =
    start.format("YYYYMMDD") === end.format("YYYYMMDD") ? end.format("HH:mm") : end.format("M月D日(ddd) HH:mm");

  return `${startString}〜${endString}`;
};

/**
 * @概要
 * RFC3339形式のフォーマットの文字列を「開始日時」「終了日時」の2つとして受け取り、
 * `YYYY年M月D日 HH:mm〜HH:mm` 形式に変換する関数
 * 終了日時が日を跨いでいた場合は `YYYY年M月D日 HH:mm〜YYYY年M月D日 HH:mm` 形式に変換する
 */
export const formatTimeSpanToYMDHm = (startIsoString: string | Date, endIsoString: string | Date) => {
  const start = dayjs(startIsoString).locale(ja);
  const end = dayjs(endIsoString).locale(ja);
  const startString = start.format("YYYY年M月D日 HH:mm");
  const endString =
    start.format("YYYYMMDD") === end.format("YYYYMMDD") ? end.format("HH:mm") : end.format("YYYY年M月D日 HH:mm");

  return `${startString}〜${endString}`;
};
